import { useState } from 'react';
import { useAuthForm } from '../../hooks/auth/useAuthForm';
import { useAuthSubmit } from '../../hooks/auth/useAuthSubmit';
import { useAuthModals } from '../../hooks/auth/useAuthModals';
import { useLoginRedirect } from '../../hooks/auth/useLoginRedirect';
import { useLoginPageState } from '../../hooks/auth/useLoginPageState';
import { useProfileImageHandling } from '../../hooks/profile/useProfileImageHandling';
import { LoginHero } from './LoginHero';
import { LoginFormContainer } from './LoginFormContainer';
import { EmailPasswordFields } from './EmailPasswordFields';
import { AuthModeToggle } from './AuthModeToggle';
import { AuthModalsGroup } from './AuthModalsGroup';
import { LoginProfileSkeleton } from './LoginProfileSkeleton';
import { Button } from '../../components/Button';

/**
 * Login page component - refactored to separate business logic from UI.
 * All business logic is now in custom hooks, making it reusable for React Native.
 */
export const LoginPage = () => {
  // Check if user is already logged in
  const loginState = useLoginPageState();

  // Web-specific redirect logic
  const redirect = useLoginRedirect();
  const [showMobileAuth, setShowMobileAuth] = useState(!redirect.shouldStartInSignupMode);

  // Business logic hooks (platform-agnostic)
  const form = useAuthForm(redirect.shouldStartInSignupMode);
  const auth = useAuthSubmit();
  const modals = useAuthModals();

  // Profile image handling
  const { profileImageUrl, handleImageError } = useProfileImageHandling(loginState.profile?.userId);

  // Derived content
  const title = form.isSignup ? 'Join the Adventure 🌴' : 'Welcome Back 👋';
  const subtitle = form.isSignup
    ? 'Start your journey with TripVibes today.'
    : 'Ready for your next trip?';
  const submitTitle = form.isSignup ? 'Create Account' : 'Log In';

  // Handler: Submit form
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.canSubmit) return;

    const result = await auth.submit(form.email, form.password, form.isSignup);

    if (result.success) {
      redirect.redirectAfterSuccess();
    } else if (result.needsVerification) {
      modals.verification.open();
    } else if (result.inviteOnly) {
      modals.inviteOnly.open();
    } else {
      modals.failureNotification.open(result.error || 'Wrong username or password');
    }
  };

  // Handler: Login after verification
  const handleLoginAfterVerification = async () => {
    const result = await auth.submit(form.email, form.password, false);
    if (result.success) {
      redirect.redirectAfterSuccess();
    } else if (result.error) {
      modals.failureNotification.open(result.error);
    }
  };

  // Handler: Forgot password success
  // No additional notification needed - the modal already shows success screen
  const handleForgotPasswordSuccess = () => {
    // Modal closes automatically, user can now log in with new password
  };

  return (
    <div className="flex min-h-screen bg-surface">
      {/* Overlay Logo */}
      <img
        className="absolute top-8 left-8 z-20 h-10 w-auto"
        src="/logo_side_full_big.png"
        alt="TripVibes"
        style={{ filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.4))' }}
      />

      {/* Left Column - Hero/Visuals */}
      <LoginHero
        showMobile={!showMobileAuth}
        onGetStarted={() => {
          form.setIsSignup(true);
          form.setPassword('');
          form.setPasswordRepeat('');
          setShowMobileAuth(true);
        }}
        onLogin={() => {
          form.setIsSignup(false);
          form.setPassword('');
          form.setPasswordRepeat('');
          setShowMobileAuth(true);
        }}
      />

      {/* Right Column - Form or Logged-in State */}
      <LoginFormContainer
        title={loginState.isProbablyLoggedIn ? 'Already Logged In' : title}
        subtitle={loginState.isProbablyLoggedIn ? 'Continue to your trips' : subtitle}
        showMobile={showMobileAuth}
        showBack={showMobileAuth && !loginState.isProbablyLoggedIn}
        onBack={() => setShowMobileAuth(false)}
      >
        {loginState.isProbablyLoggedIn && loginState.isLoading ? (
          // Loading State: Show skeleton while fetching profile
          <LoginProfileSkeleton />
        ) : loginState.isLoggedIn ? (
          // Logged-in State: Show profile and continue button
          <div className="space-y-6">
            {/* Profile Picture */}
            <div className="flex justify-center">
              <div className="relative">
                <img
                  src={profileImageUrl}
                  alt={loginState.profile?.username}
                  onError={handleImageError}
                  className="w-24 h-24 rounded-full object-cover border-4 border-brand shadow-lg"
                />
              </div>
            </div>

            {/* Continue Button */}
            <Button variant="primary" fullWidth onClick={loginState.continueToHome} size="lg">
              Continue as {loginState.profile?.username}
            </Button>

            {/* Logout Link */}
            <div className="text-center">
              <button
                onClick={loginState.logout}
                className="text-sm text-muted hover:text-action-primary transition-colors duration-200"
              >
                Log out
              </button>
            </div>
          </div>
        ) : (
          // Not Logged In: Show login/signup form
          <>
            <form onSubmit={handleSubmit} className="space-y-6">
              <EmailPasswordFields
                email={form.email}
                password={form.password}
                passwordRepeat={form.passwordRepeat}
                isSignup={form.isSignup}
                isProcessing={auth.isProcessing}
                passwordValid={form.passwordValid}
                onEmailChange={form.setEmail}
                onPasswordChange={form.setPassword}
                onPasswordRepeatChange={form.setPasswordRepeat}
                onPasswordValidChange={form.setPasswordValid}
                onForgotPasswordClick={modals.forgotPassword.open}
              />

              <div>
                <Button
                  type="submit"
                  variant="primary"
                  fullWidth
                  disabled={!form.canSubmit || auth.isProcessing}
                  loading={auth.isProcessing}
                  keepColorWhenDisabled={form.isSignup}
                >
                  {auth.isProcessing ? 'Loading...' : submitTitle}
                </Button>
              </div>
            </form>

            <AuthModeToggle isSignup={form.isSignup} onToggle={form.toggleMode} />
          </>
        )}
      </LoginFormContainer>

      {/* Modals */}
      <AuthModalsGroup
        modals={modals}
        email={form.email}
        onLoginAfterVerification={handleLoginAfterVerification}
        onForgotPasswordSuccess={handleForgotPasswordSuccess}
      />
    </div>
  );
};
